import { createAffiliationRequest } from '@api/affiliationRequest';
import useToastContext from '@contexts/ToastContext/hook';
import { AffiliationConfigModes } from '@interfaces/affiliationConfig';
import {
  affiliationRequestToCreateSchema,
  AffiliationRequestToCreate,
} from '@interfaces/affiliationRequest';
import { Button } from '@vibTheme/components/_base/Button';
import {
  InputExternalValidation,
  InputText,
  InputTextArea,
} from '@vibTheme/components/_base/Input';
import { Typography } from '@vibTheme/components/_base/Typography';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { AffiliationMarketProductProps } from './AffiliationMarketProductCard';
import { IMAGE_NOT_FOUND } from '@utils/imageNotFound';
import { Image } from '@vibTheme/components/_base/Image';
import { useExternalValidationResolver } from '@hooks/useExternalValidationResolver';
import { isAffiliationPixKeyValid } from '@interfaces/affiliation';
import { AffiliationMarketProductInfo } from './AffiliationMarketProductInfo';
import useGroupsContext from '@contexts/GroupsContext/hook';
import { PixKeyFormatHelper } from '@components/PixKeyFormatHelper';
import { Help } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { VibTooltip } from '@vibTheme/components/VibTooltip';

export const CreateAffiliationRequestForm = (
  props: AffiliationMarketProductProps,
) => {
  const { product } = props;
  const [isCreating, setIsCreating] = useState(false);

  const { fetchGroups } = useGroupsContext();
  const toast = useToastContext();
  const router = useRouter();

  const {
    externalValidationResolver,
    isLoading: isExternalValidationLoading,
    isValid: isExternalValidationValid,
  } = useExternalValidationResolver(affiliationRequestToCreateSchema, [
    { name: 'pix', validate: isAffiliationPixKeyValid(product.id) },
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<AffiliationRequestToCreate>({
    mode: 'onChange',
    resolver: externalValidationResolver,
  });

  const onSubmit: SubmitHandler<AffiliationRequestToCreate> = async (data) => {
    setIsCreating(true);

    try {
      await createAffiliationRequest(product.botConfigId, data);
      await fetchGroups();
      toast.setContent(`Solicitação de afiliação criada com sucesso!`);

      if (product.mode === AffiliationConfigModes['Liberado para todos'])
        router.push(`/products/${product.productCode}/outreachLinks`);
      else if (product.mode === AffiliationConfigModes['Mediante aprovação'])
        router.push(`/affiliation/affiliationRequests`);
    } catch (err: any) {
      const errMessage =
        err.detail || 'Ocorreu um erro ao criar a solicitação de afiliação.';
      errMessage === 'O usuário já é afiliado da comunidade requisitada.'
        ? toast.setContent(
            'Você já é afiliado deste produto! Você pode acessar o link de vendas pelo menu Comunidades ou Afiliações > Comunidades Afiliadas.',
            'warning',
          )
        : toast.setContent(errMessage, 'error');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex gap-y-md w-full">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-md w-full "
      >
        <Typography component="h4">
          Formulário para pedido de afiliação
        </Typography>
        <div className="flex flex-col gap-md md:flex-row">
          <div className="relative w-full md:w-1/3 md:min-w-[12rem] md:max-w-[16rem] h-[18rem] rounded-default">
            <Image
              src={product.image || IMAGE_NOT_FOUND}
              alt={`Imagem de ${product.name}`}
              className={`${
                product.image ? 'object-cover' : 'object-contain'
              } rounded-t-default`}
              fill
            />
          </div>
          <div className="flex flex-col gap-md w-full">
            <InputText
              name="contact"
              control={control}
              label="Contato"
              error={!!errors.contact}
              helperText={errors.contact?.message}
            />

            <InputExternalValidation
              name="pix"
              control={control}
              label="Chave Pix"
              error={!!errors.pix}
              helperText={errors.pix?.message}
              isLoading={isExternalValidationLoading.pix}
              isValid={isExternalValidationValid.pix}
              InputProps={{
                endAdornment: (
                  <VibTooltip title={<PixKeyFormatHelper />}>
                    <IconButton>
                      <Help />
                    </IconButton>
                  </VibTooltip>
                ),
              }}
            />
            <InputTextArea
              name="message"
              control={control}
              label="Mensagem"
              error={!!errors.message}
              helperText={errors.message?.message}
            />
          </div>
        </div>
        <AffiliationMarketProductInfo
          descriptionLabel="Descrição da comunidade"
          product={product}
        />
        <Button
          type="submit"
          className="w-full md:w-32 h-[2.7rem] ml-auto"
          loading={isCreating}
          disabled={!isValid}
        >
          Enviar
        </Button>
      </form>
    </div>
  );
};
